import { Link } from "react-router-dom";
import PublicPageShell, { BRAND, PageTitle, Section, NeedsReview } from "./PublicPageShell";

/**
 * Privacy, at /privacy.
 *
 * <p><b>Written from the code, not from a template.</b> Every collection described here was checked
 * against what actually runs: the contact form stores name/email/phone/business/category/message
 * plus the raw IP address (PublicContactController); marketplace browsing stores a truncated
 * SHA-256 of IP + user-agent rather than the address itself (PublicMarketplaceController.VisitorHash);
 * a business viewing its own profile is not counted at all. A privacy policy that describes a
 * different system from the one running is worse than none — it is a statement of fact that happens
 * to be false.</p>
 *
 * <p><b>It is not a substitute for legal review.</b> Retention periods, access and correction
 * rights, overseas disclosure and the complaints pathway are obligations under the Privacy Act with
 * specific requirements, and they are marked as needing Matt's decision and a lawyer's eye rather
 * than filled with confident-sounding text.</p>
 */
export default function PartnerNetworkPrivacy() {
  return (
    <PublicPageShell
      title="Privacy"
      description="What DAconnect collects, why, and what we do with it."
    >
      <PageTitle
        title="Privacy"
        intro="What we collect, why we collect it, and who else sees it. This describes what the system actually does — not a generic template."
      />

      <NeedsReview>
        <strong>This page has not been through legal review.</strong> The collections described below
        are accurate — I checked each one against the code — but an Australian privacy policy also
        needs retention periods, access and correction rights, overseas disclosure and a complaints
        pathway, and those are decisions for you plus a lawyer rather than something I should draft.
        The gaps are marked further down.
      </NeedsReview>

      <Section heading="If you're just browsing the marketplace">
        <p style={{ margin: "0 0 12px" }}>
          You don't need an account, and we don't ask for your name or email.
        </p>
        <p style={{ margin: "0 0 12px" }}>
          We do count how often each business profile is viewed, and roughly how many distinct people
          viewed it, so a member can see whether their listing is working. To tell one visitor from
          another without identifying anybody, we take your IP address and browser user-agent, run
          them through a one-way hash and keep only part of the result.{" "}
          <strong style={{ color: "#243139" }}>Your IP address itself is not stored</strong>, and the
          hash can't be turned back into it.
        </p>
        <p style={{ margin: 0 }}>
          We also count how often a business appeared in search results. A business viewing its own
          profile isn't counted at all.
        </p>
      </Section>

      <Section heading="If you contact a business through the marketplace">
        <p style={{ margin: 0 }}>
          Your enquiry — and the contact details you put in it — goes to that business so they can
          reply. We keep a record so both sides can refer back to it, and so the business can mark
          what came of it. We don't sell your enquiry, and we don't send it to anyone else.
        </p>
      </Section>

      <Section heading="If you use the contact form">
        <p style={{ margin: 0 }}>
          We store your name, email address, and any phone number, business name and message you
          give us, along with the date and{" "}
          <strong style={{ color: "#243139" }}>your IP address</strong>, which is kept to help us deal
          with abuse of the form. It's emailed to our team and readable by our staff.
        </p>
      </Section>

      <Section heading="If you have an account">
        <p style={{ margin: "0 0 12px" }}>
          We store what you give us: your name and email, your business's details, the council areas
          you work in, and your ABN. Most of that is published on your profile — that's the point of
          a listing — and you control it from your settings.
        </p>
        <p style={{ margin: 0 }}>
          We record when you last signed in, and we keep a log of emails we've sent you and whether
          they were opened, so we can tell a delivery problem from an unread message.
        </p>
      </Section>

      <Section heading="Payments">
        <p style={{ margin: 0 }}>
          Subscriptions are handled by <strong style={{ color: "#243139" }}>Stripe</strong>.{" "}
          Card numbers are entered directly with Stripe and{" "}
          <strong style={{ color: "#243139" }}>never reach DAconnect's servers</strong>. We keep
          Stripe's customer and subscription identifiers, what plan you're on and your invoice
          history. Stripe has its own privacy policy covering what it does with your payment details.
        </p>
      </Section>

      <Section heading="Who else sees your information">
        <p style={{ margin: "0 0 12px" }}>
          We use third parties to run the service: Stripe for payments, an email provider to send
          mail, cloud hosting and storage, and mapping services for addresses and coordinates. They
          see only what they need to do their part.
        </p>
        <p style={{ margin: 0 }}>
          We don't sell your personal information, and we don't hand it to advertisers.
        </p>
      </Section>

      <Section heading="Emails and unsubscribing">
        <p style={{ margin: 0 }}>
          Every marketing email we send carries an unsubscribe link, and unsubscribing stops that
          mail for good. We'll still send you service messages about your own account — receipts,
          billing problems, security notices — because you need those to use the product.
        </p>
      </Section>

      <NeedsReview>
        Still to be decided and written, with advice:
        <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
          <li><strong>Retention</strong> — how long enquiries, contact submissions, stats and closed accounts are kept.</li>
          <li><strong>Access and correction</strong> — how someone asks for their data or a deletion, and who handles it.</li>
          <li><strong>Overseas disclosure</strong> — several of the providers above store data outside Australia; APP 8 requires that to be stated.</li>
          <li><strong>Complaints</strong> — the contact point, and the escalation path to the OAIC.</li>
          <li><strong>Cookies</strong> — what the app sets and whether any consent banner is required.</li>
        </ul>
      </NeedsReview>

      <Section heading="Contact">
        <p style={{ margin: 0 }}>
          DAconnect Pty Ltd, ABN 74 168 911 402. Questions about any of this —{" "}
          <Link to="/contact" style={{ color: BRAND, fontWeight: 700 }}>get in touch</Link>.
        </p>
      </Section>
    </PublicPageShell>
  );
}
